/**
 * React Query hooks for personal goals
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@lib/api';
import { useAuth } from '@lib/auth';
import type { GoalsResponse, GoalSuggestions } from '@lib/types';

// Query keys
export const goalKeys = {
  all: ['goals'] as const,
  current: () => [...goalKeys.all, 'current'] as const,
  suggestions: () => [...goalKeys.all, 'suggestions'] as const,
};

// Fetch current week's goals
export function useGoals() {
  const { isAuthenticated, isLoading } = useAuth();
  
  return useQuery({
    queryKey: goalKeys.current(),
    queryFn: () => api.get<GoalsResponse>('/api/goals'),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // Refetch every minute
    enabled: isAuthenticated && !isLoading, // Only fetch when authenticated
  });
}

// Fetch goal suggestions
export function useGoalSuggestions() {
  const { isAuthenticated, isLoading } = useAuth();
  
  return useQuery({
    queryKey: goalKeys.suggestions(),
    queryFn: () => api.get<GoalSuggestions>('/api/goals/suggestions'),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: isAuthenticated && !isLoading, // Only fetch when authenticated
  });
}

// Create goal mutation
export function useCreateGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { activityTypeId: string; targetQuantity: number }) =>
      api.post<{ id: string; activityName: string; isCompleted: boolean }>('/api/goals', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalKeys.current() });
    },
  });
}

// Delete goal mutation
export function useDeleteGoal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalId: string) => api.delete<{ deleted: boolean }>(`/api/goals/${goalId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalKeys.current() });
    },
  });
}
